/**
 * FlagIcon — inline language-flag icon rendered from vendored Twemoji SVGs.
 *
 * Replaces flag emoji (🇵🇱 🇬🇧 🇩🇪 🇨🇿): Windows ships no flag-emoji glyphs, so
 * emoji flags render there as bare letter pairs ("PL"). Twemoji is the same
 * emoji artwork as SVG, so an <img> renders identically on every OS.
 *
 * Sizing model: each Twemoji flag sits on a square 36×36 canvas with the flag
 * artwork inset (36w × 26h, rounded corners baked in) — the same proportions
 * as an emoji glyph cell. Rendering the square at `size × size` therefore
 * matches the footprint of the emoji at `fontSize: size`, so swapping one for
 * the other does not shift layout.
 *
 * Assets: admin/src/assets/flags/*.svg (Twemoji v15.1.0, CC-BY 4.0 — see
 * LICENSE.md next to them). Vite inlines them as data URIs at build time
 * (each < 4 KB), so no extra network requests.
 */
import type { CSSProperties, JSX } from 'react';
import type { LangCode } from '@/lib/supported-langs';
import plFlag from '@/assets/flags/pl.svg';
import gbFlag from '@/assets/flags/gb.svg';
import deFlag from '@/assets/flags/de.svg';
import czFlag from '@/assets/flags/cz.svg';

/** lang code → vendored Twemoji asset (en intentionally maps to the GB flag). */
const FLAG_SRC: Record<LangCode, string> = {
  pl: plFlag,
  en: gbFlag,
  de: deFlag,
  cs: czFlag,
};

export interface FlagIconProps {
  /** Language whose flag to render. */
  lang: LangCode;
  /**
   * Square size in px — pass the `fontSize` the replaced emoji had so the
   * footprint stays identical. Defaults to 16 (≈1em).
   */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Decorative by default (`alt=""` + `aria-hidden`): every usage sits next to
 * a visible/accessible language name or code, so the flag carries no
 * information of its own — exactly like the emoji it replaces.
 */
export function FlagIcon({ lang, size = 16, className, style }: FlagIconProps): JSX.Element {
  return (
    <img
      src={FLAG_SRC[lang]}
      alt=""
      aria-hidden="true"
      draggable={false}
      width={size}
      height={size}
      className={className}
      style={{
        display: 'inline-block',
        // Twemoji's canonical inline alignment — sits on the text baseline
        // like an emoji glyph. Irrelevant inside flex rows (they center),
        // load-bearing in plain inline-text contexts.
        verticalAlign: '-0.1em',
        // Artwork already has ~2px rounded corners baked in; this only guards
        // the box edge so the icon stays tidy if an asset is ever swapped.
        borderRadius: 2,
        // Emoji text never shrank in flex pills; the img must not either.
        flexShrink: 0,
        ...style,
      }}
    />
  );
}
